import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Package, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

const OrderHistoryScreen = () => {
  const { user, getConfig } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/orders/myorders`, getConfig());
        setOrders(data);
      } catch (err) {
        console.error('Error fetching orders:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchOrders();
  }, [user, getConfig]);

  return (
    <div className="container mx-auto px-4 py-12 max-w-5xl">
      <div className="space-y-2 mb-10">
        <h1 className="text-3xl font-black italic text-secondary-900 uppercase">Order History</h1>
        <div className="h-1.5 w-20 bg-primary-500 rounded-full" />
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => <div key={i} className="bg-secondary-100 animate-pulse rounded-3xl h-32" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20 space-y-4">
          <Package className="h-16 w-16 text-secondary-200 mx-auto" />
          <h2 className="text-2xl font-bold text-secondary-400">No orders yet</h2>
          <p className="text-secondary-500">Start shopping to see your orders here.</p>
          <Link to="/" className="inline-block bg-primary-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-primary-700 transition-all">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order._id} className="bg-white rounded-[40px] border border-secondary-100 p-8 hover:shadow-xl transition-all group">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-secondary-400 uppercase tracking-widest">Order ID: #{order._id.slice(-8)}</p>
                  <p className="text-sm text-secondary-500 font-bold">{new Date(order.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                </div>
                <div className="flex items-center space-x-6">
                  <span className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.1em] ${
                    order.isDelivered ? 'bg-emerald-100 text-emerald-700' :
                    order.isPaid ? 'bg-blue-100 text-blue-700' :
                    'bg-amber-100 text-amber-700'
                  }`}>
                    {order.isDelivered ? 'Delivered' : order.isPaid ? 'Shipped' : 'Processing'}
                  </span>
                  <span className="text-2xl font-black text-secondary-900 tracking-tighter">₹{order.totalPrice}</span>
                </div>
              </div>

              <div className="flex items-center space-x-4 overflow-x-auto pb-4 scrollbar-hide">
                {order.orderItems.map((item, i) => (
                  <div key={i} className="flex-shrink-0 relative group/img">
                    <img src={item.image} alt={item.name} className="w-20 h-20 rounded-2xl object-cover border border-secondary-100 shadow-sm" />
                    <div className="absolute inset-0 bg-secondary-900/40 rounded-2xl opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center">
                       <span className="text-white text-[10px] font-black">×{item.qty}</span>
                    </div>
                  </div>
                ))}
                <div className="flex-shrink-0 pl-4 text-xs font-black text-secondary-300 uppercase tracking-widest">
                  {order.orderItems.length} item{order.orderItems.length > 1 ? 's' : ''}
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-secondary-50 flex items-center justify-between">
                <div className="flex items-center space-x-6 text-[10px] font-black uppercase tracking-widest">
                  <div className="flex items-center space-x-2">
                    <span className="text-secondary-400">Payment:</span>
                    {order.isPaid ? <span className="text-emerald-600">Verified</span> : <span className="text-red-500">Pending</span>}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-secondary-400">Status:</span>
                    {order.isDelivered ? <span className="text-emerald-600">Success</span> : <span className="text-amber-600">In Progress</span>}
                  </div>
                </div>
                <Link to={`/orders/${order._id}`} className="text-primary-600 font-black text-[10px] uppercase tracking-widest flex items-center space-x-1 hover:space-x-2 transition-all">
                  <span>View Details</span>
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default OrderHistoryScreen;
